import {
  Loader2,
  Mic,
  Pause,
  Play,
  SendHorizontal,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language";
import { hapticImpact } from "@/lib/haptics";

// Voice-note recorder — full port of the reference chat-audio-recorder.tsx:
// live canvas waveform, pause/resume, playback preview of the take (Web Audio
// decode with play/pause and drag-to-scrub), plus the webm/opus → Ogg/opus
// repackaging Meta requires. Colors mapped to the app tokens.

// sampling cadence for the live waveform: one amplitude bar every ~90ms.
const SAMPLE_MS = 90;
// media recorder flush interval so partial audio is available for playback
// while paused, without waiting for the final stop.
const TIMESLICE_MS = 250;

// Prefer a native Ogg/opus recording (Firefox); Chrome/Safari fall back to
// webm/opus which we repackage to Ogg below, then to mp4 as a last resort
// (sent as-is — Meta accepts audio/mp4 too).
const MIME_CANDIDATES = [
  "audio/ogg;codecs=opus",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  return (
    MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? ""
  );
};

const extForMime = (mime: string) => {
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
};

const formatDuration = (seconds: number) => {
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// webm/opus -> Ogg/opus repackaging
//
// Repackage the opus audio inside a MediaRecorder webm blob into an Ogg
// container (audio/ogg; codecs=opus). Chrome can only record opus into webm,
// but WhatsApp (and most voice-note pipelines) require Ogg — the opus packets
// are identical, so this is a lossless container rewrite, not a re-encode.
// ---------------------------------------------------------------------------

// Ogg uses a CRC32 with polynomial 0x04C11DB7, MSB-first, no reflection.
const OGG_CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) {
      r = (r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1) >>> 0;
    }
    table[i] = r >>> 0;
  }
  return table;
})();

const oggCrc = (data: Uint8Array) => {
  let crc = 0;
  for (let i = 0; i < data.length; i++) {
    crc = ((crc << 8) ^ OGG_CRC_TABLE[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  }
  return crc >>> 0;
};

// opus frame duration per TOC config, expressed in 48 kHz samples.
const OPUS_FRAME_SAMPLES = [
  480, 960, 1920, 2880, 480, 960, 1920, 2880, 480, 960, 1920, 2880, 480, 960,
  480, 960, 120, 240, 480, 960, 120, 240, 480, 960, 120, 240, 480, 960, 120,
  240, 480, 960,
];

const opusPacketSamples = (packet: Uint8Array) => {
  if (packet.length === 0) return 0;
  const toc = packet[0];
  const config = toc >> 3;
  const code = toc & 0x03;
  let frames = 1;
  if (code === 1 || code === 2) frames = 2;
  else if (code === 3) frames = packet.length > 1 ? packet[1] & 0x3f : 1;
  return OPUS_FRAME_SAMPLES[config] * frames;
};

interface Vint {
  value: number;
  length: number;
}

const readVint = (data: Uint8Array, pos: number, keepMarker: boolean): Vint => {
  const first = data[pos];
  let mask = 0x80;
  let length = 1;
  while (length <= 8 && !(first & mask)) {
    mask >>= 1;
    length++;
  }
  if (length > 8) return { value: 0, length: 1 };
  let value = keepMarker ? first : first & (mask - 1);
  for (let i = 1; i < length; i++) value = value * 256 + data[pos + i];
  return { value, length };
};

// data-bits all set marks an "unknown" size (streamed master elements).
const isUnknownSize = (value: number, length: number) =>
  value === Math.pow(2, 7 * length) - 1;

const ID_SEGMENT = 0x18538067;
const ID_TRACKS = 0x1654ae6b;
const ID_TRACK_ENTRY = 0xae;
const ID_CODEC_PRIVATE = 0x63a2;
const ID_CLUSTER = 0x1f43b675;
const ID_BLOCK_GROUP = 0xa0;
const ID_SIMPLE_BLOCK = 0xa3;
const ID_BLOCK = 0xa1;

const MASTERS = new Set([
  ID_SEGMENT,
  ID_TRACKS,
  ID_TRACK_ENTRY,
  ID_CLUSTER,
  ID_BLOCK_GROUP,
]);

interface Extracted {
  opusHead: Uint8Array | null;
  packets: Uint8Array[];
  laced: boolean;
}

const walkEbml = (
  data: Uint8Array,
  start: number,
  end: number,
  out: Extracted,
) => {
  let pos = start;
  while (pos < end - 1) {
    const id = readVint(data, pos, true);
    pos += id.length;
    if (pos >= end) break;
    const size = readVint(data, pos, false);
    pos += size.length;
    const dataStart = pos;
    const dataEnd = isUnknownSize(size.value, size.length)
      ? end
      : Math.min(end, dataStart + size.value);

    if (MASTERS.has(id.value)) {
      walkEbml(data, dataStart, dataEnd, out);
    } else if (id.value === ID_CODEC_PRIVATE) {
      if (!out.opusHead) out.opusHead = data.subarray(dataStart, dataEnd);
    } else if (id.value === ID_SIMPLE_BLOCK || id.value === ID_BLOCK) {
      const track = readVint(data, dataStart, false);
      const flagsPos = dataStart + track.length + 2; // + int16 timecode
      const lacing = (data[flagsPos] >> 1) & 0x03;
      const frameStart = flagsPos + 1;
      if (lacing === 0) {
        out.packets.push(data.subarray(frameStart, dataEnd));
      } else {
        out.laced = true;
      }
    }
    pos = dataEnd;
  }
};

const writeUint32LE = (arr: Uint8Array, off: number, value: number) => {
  arr[off] = value & 0xff;
  arr[off + 1] = (value >>> 8) & 0xff;
  arr[off + 2] = (value >>> 16) & 0xff;
  arr[off + 3] = (value >>> 24) & 0xff;
};

const writeUint64LE = (arr: Uint8Array, off: number, value: number) => {
  let v = value;
  for (let i = 0; i < 8; i++) {
    arr[off + i] = v & 0xff;
    v = Math.floor(v / 256);
  }
};

const SERIAL = 0x5245_4b5a; // "REKZ"

const buildPage = (
  headerType: number,
  granule: number,
  seq: number,
  packets: Uint8Array[],
): Uint8Array => {
  const segTable: number[] = [];
  for (const pk of packets) {
    let len = pk.length;
    while (len >= 255) {
      segTable.push(255);
      len -= 255;
    }
    segTable.push(len);
  }
  const dataLen = packets.reduce((s, p) => s + p.length, 0);
  const page = new Uint8Array(27 + segTable.length + dataLen);
  page[0] = 0x4f; // O
  page[1] = 0x67; // g
  page[2] = 0x67; // g
  page[3] = 0x53; // S
  page[4] = 0; // version
  page[5] = headerType;
  writeUint64LE(page, 6, granule);
  writeUint32LE(page, 14, SERIAL);
  writeUint32LE(page, 18, seq);
  // CRC (22..25) left zero for the checksum computation
  page[26] = segTable.length;
  page.set(segTable, 27);
  let off = 27 + segTable.length;
  for (const pk of packets) {
    page.set(pk, off);
    off += pk.length;
  }
  writeUint32LE(page, 22, oggCrc(page));
  return page;
};

const textBytes = (text: string) => {
  const arr = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) arr[i] = text.charCodeAt(i);
  return arr;
};

const buildOpusTags = () => {
  const vendor = textBytes("mandoob-inbox");
  const tags = new Uint8Array(8 + 4 + vendor.length + 4);
  tags.set(textBytes("OpusTags"), 0);
  writeUint32LE(tags, 8, vendor.length);
  tags.set(vendor, 12);
  writeUint32LE(tags, 12 + vendor.length, 0); // 0 user comments
  return tags;
};

/**
 * Convert a MediaRecorder webm/opus blob to an Ogg/opus blob. Returns null if
 * the input can't be parsed (caller should fall back to the original blob).
 */
async function webmOpusToOgg(input: Blob): Promise<Blob | null> {
  try {
    const data = new Uint8Array(await input.arrayBuffer());
    const extracted: Extracted = { opusHead: null, packets: [], laced: false };
    walkEbml(data, 0, data.length, extracted);

    if (
      extracted.laced ||
      !extracted.opusHead ||
      extracted.packets.length === 0
    ) {
      return null;
    }

    const pages: Uint8Array[] = [];
    let seq = 0;
    // BOS page: OpusHead identification header.
    pages.push(buildPage(0x02, 0, seq++, [extracted.opusHead]));
    // OpusTags comment header on its own page.
    pages.push(buildPage(0, 0, seq++, [buildOpusTags()]));

    // audio pages: pack packets while respecting the 255-segment page limit.
    const { packets } = extracted;
    let granule = 0;
    let i = 0;
    while (i < packets.length) {
      const pagePackets: Uint8Array[] = [];
      let segments = 0;
      while (i < packets.length) {
        const need = Math.floor(packets[i].length / 255) + 1;
        if (segments + need > 255) break;
        segments += need;
        granule += opusPacketSamples(packets[i]);
        pagePackets.push(packets[i]);
        i++;
      }
      const isLast = i >= packets.length;
      pages.push(buildPage(isLast ? 0x04 : 0, granule, seq++, pagePackets));
    }

    return new Blob(pages, { type: "audio/ogg" });
  } catch {
    return null;
  }
}

type Phase = "loading" | "recording" | "paused" | "error";

export function AudioRecorder({
  onFinish,
  onCancel,
}: {
  onFinish: (file: File) => void;
  onCancel: () => void;
}) {
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>("loading");
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef("");

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const barsRef = useRef<number[]>([]);
  const lastSampleRef = useRef(0);

  const startedAtRef = useRef(0);
  const accumulatedRef = useRef(0);

  // playback runs through the Web Audio API off a decoded AudioBuffer rather
  // than an <audio> element: MediaRecorder webm blobs seek slowly and report
  // an Infinity duration, which broke instant replay and scrubbing. A buffer
  // source starts at any offset immediately.
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const playStartCtxRef = useRef(0);
  const playOffsetRef = useRef(0);
  const playingRef = useRef(false);
  const playheadRef = useRef(0);
  const durationRef = useRef(0);
  const scrubbingRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const scrubMoveRef = useRef<((event: PointerEvent) => void) | null>(null);
  const scrubUpRef = useRef<((event: PointerEvent) => void) | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const phaseRef = useRef<Phase>("loading");
  phaseRef.current = phase;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const barW = 3;
    const gap = 2;
    const step = barW + gap;
    const maxBars = Math.max(1, Math.floor(w / step));
    const source = barsRef.current;

    // always resample the whole recording to exactly `maxBars` bars so the
    // waveform spans the full canvas width at all times: peak-per-bucket to
    // compact once there is more data than bars, linear interpolation to
    // stretch the few early samples across the width.
    const bars: number[] = [];
    if (source.length === 0) {
      // nothing recorded yet
    } else if (source.length >= maxBars) {
      for (let i = 0; i < maxBars; i++) {
        let peak = 0;
        const from = Math.floor((i * source.length) / maxBars);
        const to = Math.floor(((i + 1) * source.length) / maxBars);
        for (let j = from; j < Math.max(from + 1, to); j++) {
          peak = Math.max(peak, source[j]);
        }
        bars.push(peak);
      }
    } else {
      const denom = maxBars > 1 ? maxBars - 1 : 1;
      const span = source.length - 1;
      for (let i = 0; i < maxBars; i++) {
        const p = (i / denom) * span;
        const lo = Math.floor(p);
        const hi = Math.min(source.length - 1, lo + 1);
        bars.push(source[lo] * (1 - (p - lo)) + source[hi] * (p - lo));
      }
    }

    const styles = getComputedStyle(canvas);
    const fill = styles.getPropertyValue("--foreground").trim() || "#0a0a0a";
    const idle = styles.getPropertyValue("--border").trim() || "#cbd5e1";
    const progressBars =
      phaseRef.current === "paused"
        ? playheadRef.current * bars.length
        : bars.length;

    const mid = h / 2;
    for (let i = 0; i < bars.length; i++) {
      const amp = Math.min(1, bars[i]);
      const barH = Math.max(2, amp * (h - 2));
      const x = i * step;
      const y = mid - barH / 2;
      ctx.fillStyle = i < progressBars ? fill : idle;
      const r = barW / 2;
      ctx.beginPath();
      ctx.roundRect(x, y, barW, barH, r);
      ctx.fill();
    }

    // draggable playback knob over the waveform while paused.
    if (phaseRef.current === "paused") {
      const kr = Math.min(7, mid - 1);
      const kx = Math.max(kr, Math.min(w - kr, playheadRef.current * w));
      ctx.beginPath();
      ctx.arc(kx, mid, kr, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
    }
  }, []);

  // keep the waveform filling the wrapper when its width changes (composer
  // resize, window resize) even while idle between frames.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [phase, draw]);

  const stopWaveLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const runWaveLoop = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (let i = 0; i < data.length; i++) {
        const v = Math.abs(data[i] - 128) / 128;
        if (v > peak) peak = v;
      }
      const now = performance.now();
      if (now - lastSampleRef.current >= SAMPLE_MS) {
        lastSampleRef.current = now;
        barsRef.current.push(peak);
      }
      setElapsed(
        (accumulatedRef.current + (performance.now() - startedAtRef.current)) /
          1000,
      );
      draw();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [draw]);

  // tear down the current buffer source without firing its onended handler.
  const stopSource = useCallback(() => {
    const source = sourceRef.current;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
      source.disconnect();
      sourceRef.current = null;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    stopSource();
    playingRef.current = false;
    playOffsetRef.current = 0;
    playheadRef.current = 0;
    setPlaying(false);
  }, [stopSource]);

  const endScrub = useCallback(() => {
    if (scrubMoveRef.current)
      window.removeEventListener("pointermove", scrubMoveRef.current);
    if (scrubUpRef.current) {
      window.removeEventListener("pointerup", scrubUpRef.current);
      window.removeEventListener("pointercancel", scrubUpRef.current);
    }
    scrubMoveRef.current = null;
    scrubUpRef.current = null;
    scrubbingRef.current = false;
  }, []);

  const cleanup = useCallback(() => {
    stopWaveLoop();
    endScrub();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    stopSource();
    audioBufferRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
  }, [stopWaveLoop, stopSource, endScrub]);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        mimeRef.current = pickMimeType();

        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        audioCtx.createMediaStreamSource(stream).connect(analyser);
        analyserRef.current = analyser;

        const recorder = new MediaRecorder(
          stream,
          mimeRef.current ? { mimeType: mimeRef.current } : undefined,
        );
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorderRef.current = recorder;
        recorder.start(TIMESLICE_MS);

        startedAtRef.current = performance.now();
        lastSampleRef.current = 0;
        // The mic is live now — the driver is usually already talking, so say
        // so with a buzz rather than only with the on-screen indicator.
        hapticImpact("medium");
        setPhase("recording");
        runWaveLoop();
      } catch {
        if (!cancelled) setPhase("error");
      }
    };

    void start();

    return () => {
      cancelled = true;
      cleanup();
    };
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // decode the recorded blob into an AudioBuffer for gapless play/seek/replay.
  const decodeRecording = useCallback(async () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    if (blob.size === 0) return;
    try {
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      audioBufferRef.current = buffer;
      durationRef.current = buffer.duration;
    } catch {
      /* keep the timer-based duration; playback stays unavailable */
    }
  }, []);

  const pause = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    hapticImpact("light");
    accumulatedRef.current += performance.now() - startedAtRef.current;
    try {
      recorder.requestData();
    } catch {
      /* not all browsers allow requestData mid-stream */
    }
    recorder.pause();
    stopWaveLoop();
    durationRef.current = accumulatedRef.current / 1000;
    playOffsetRef.current = 0;
    playheadRef.current = 0;
    setElapsed(durationRef.current);
    setPhase("paused");
    // let the flushed chunk land before decoding the preview.
    setTimeout(() => void decodeRecording(), 0);
    draw();
  };

  const resume = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    hapticImpact("light");
    stopPlayback();
    audioBufferRef.current = null;
    startedAtRef.current = performance.now();
    recorder.resume();
    setPhase("recording");
    runWaveLoop();
  };

  // current playback position in seconds, whether playing or paused mid-clip.
  const playbackPosition = () => {
    const ctx = audioCtxRef.current;
    if (playingRef.current && ctx) {
      return Math.min(
        durationRef.current,
        playOffsetRef.current + (ctx.currentTime - playStartCtxRef.current),
      );
    }
    return playOffsetRef.current;
  };

  const runPlayheadLoop = useCallback(() => {
    const tick = () => {
      if (!scrubbingRef.current) {
        const pos = playbackPosition();
        playheadRef.current =
          durationRef.current > 0 ? Math.min(1, pos / durationRef.current) : 0;
        setElapsed(pos);
        draw();
      }
      if (playingRef.current) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    // playbackPosition reads only refs, safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draw]);

  const startPlaybackAt = (offset: number) => {
    const ctx = audioCtxRef.current;
    const buffer = audioBufferRef.current;
    if (!ctx || !buffer) return;
    stopSource();
    void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => {
      // natural end (manual stops null out onended first)
      stopSource();
      playingRef.current = false;
      playOffsetRef.current = 0;
      playheadRef.current = 0;
      setPlaying(false);
      stopWaveLoop();
      setElapsed(durationRef.current);
      draw();
    };
    const start = Math.max(0, Math.min(offset, buffer.duration - 0.001));
    source.start(0, start);
    sourceRef.current = source;
    playOffsetRef.current = start;
    playStartCtxRef.current = ctx.currentTime;
    playingRef.current = true;
    setPlaying(true);
    runPlayheadLoop();
  };

  const pausePlayback = () => {
    playOffsetRef.current = playbackPosition();
    stopSource();
    playingRef.current = false;
    setPlaying(false);
    stopWaveLoop();
    draw();
  };

  const togglePlayback = () => {
    if (!audioBufferRef.current) return;
    if (playingRef.current) {
      pausePlayback();
      return;
    }
    // restart from the beginning when sitting at the end (finished or
    // scrubbed to the tail) so the play button always replays the recording.
    let offset = playOffsetRef.current;
    if (offset >= durationRef.current - 0.05) offset = 0;
    startPlaybackAt(offset);
  };

  const seekToClientX = (clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const frac =
      rect.width > 0
        ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        : 0;
    playheadRef.current = frac;
    const pos = frac * durationRef.current;
    playOffsetRef.current = pos;
    setElapsed(pos);
    if (playingRef.current) startPlaybackAt(pos);
    draw();
  };

  // drive scrubbing off window listeners so the drag keeps tracking even when
  // the pointer leaves the thin waveform, survives re-renders, and can't be
  // swallowed by ancestors. playback is paused for the duration of the drag.
  const beginScrub = (clientX: number) => {
    if (phaseRef.current !== "paused") return;
    endScrub();
    scrubbingRef.current = true;
    wasPlayingRef.current = playingRef.current;
    if (playingRef.current) pausePlayback();

    const move = (event: PointerEvent) => seekToClientX(event.clientX);
    const up = () => {
      endScrub();
      if (wasPlayingRef.current) startPlaybackAt(playOffsetRef.current);
    };
    scrubMoveRef.current = move;
    scrubUpRef.current = up;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    seekToClientX(clientX);
  };

  const handleDelete = () => {
    hapticImpact("light");
    cleanup();
    onCancel();
  };

  const handleSend = () => {
    hapticImpact("light");
    stopPlayback();
    stopWaveLoop();
    const recorder = recorderRef.current;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    audioCtxRef.current?.close().catch(() => {});

    if (!recorder || recorder.state === "inactive") {
      onCancel();
      return;
    }
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mimeRef.current });
      if (blob.size === 0) {
        onCancel();
        return;
      }
      // Meta rejects webm; repackage the opus audio into Ogg before send.
      let outBlob: Blob = blob;
      let ext = extForMime(mimeRef.current);
      let type = mimeRef.current || blob.type;
      if (mimeRef.current.includes("webm")) {
        const ogg = await webmOpusToOgg(blob);
        if (ogg) {
          outBlob = ogg;
          ext = "ogg";
          type = "audio/ogg";
        }
        // Repackaging failed: the note still goes out as audio/webm — Meta may
        // refuse it, but dropping the recording silently would be worse.
      }
      const file = new File([outBlob], `voice-message.${ext}`, { type });
      onFinish(file);
    };
    recorder.stop();
  };

  return (
    // Bare row — it swaps into the composer pill's controls row, which
    // already carries the surface.
    <div className="tw:flex tw:min-h-11 tw:min-w-0 tw:flex-1 tw:items-center tw:gap-1.5">
      <button
        type="button"
        aria-label={t("Mandoob:Recorder.Cancel")}
        onClick={handleDelete}
        className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted tw:hover:text-foreground"
      >
        <Trash2 className="tw:size-5" strokeWidth={1.75} />
      </button>

      {phase === "loading" ? (
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:text-muted-foreground">
          <Loader2 className="tw:size-4 tw:shrink-0 tw:animate-spin" />
          <span className="tw:min-w-0 tw:truncate tw:text-sm">
            {t("Mandoob:Recorder.Preparing")}
          </span>
        </div>
      ) : phase === "error" ? (
        <span className="tw:min-w-0 tw:flex-1 tw:text-sm tw:text-destructive">
          {t("Mandoob:Recorder.MicBlocked")}
        </span>
      ) : (
        <>
          {phase === "paused" ? (
            <button
              type="button"
              aria-label={
                playing
                  ? t("Mandoob:Recorder.StopPlay")
                  : t("Mandoob:Recorder.Play")
              }
              onClick={togglePlayback}
              className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-foreground tw:transition-colors tw:hover:bg-muted"
            >
              {playing ? (
                <Pause
                  className="tw:size-5 tw:fill-current"
                  strokeWidth={1.75}
                />
              ) : (
                <Play
                  className="tw:size-5 tw:fill-current"
                  strokeWidth={1.75}
                />
              )}
            </button>
          ) : (
            <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center">
              <span className="tw:relative tw:flex tw:size-3">
                <span className="tw:absolute tw:inline-flex tw:size-full tw:animate-ping tw:rounded-full tw:bg-destructive tw:opacity-75" />
                <span className="tw:relative tw:inline-flex tw:size-3 tw:rounded-full tw:bg-destructive" />
              </span>
            </span>
          )}

          <span
            dir="ltr"
            className="tw:w-12 tw:shrink-0 tw:text-center tw:text-base tw:font-semibold tw:tabular-nums tw:text-foreground"
          >
            {formatDuration(elapsed)}
          </span>

          <canvas
            ref={canvasRef}
            className={
              phase === "paused"
                ? "tw:h-9 tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:touch-none"
                : "tw:h-9 tw:min-w-0 tw:flex-1"
            }
            aria-hidden="true"
            onPointerDown={
              phase === "paused"
                ? (event) => {
                    event.preventDefault();
                    beginScrub(event.clientX);
                  }
                : undefined
            }
          />

          {phase === "recording" ? (
            <button
              type="button"
              aria-label={t("Mandoob:Recorder.Pause")}
              onClick={pause}
              className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:hover:bg-destructive-soft"
            >
              <Pause
                className="tw:size-6 tw:fill-destructive tw:stroke-none"
                strokeWidth={1.75}
              />
            </button>
          ) : (
            <button
              type="button"
              aria-label={t("Mandoob:Recorder.Resume")}
              onClick={resume}
              className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-destructive tw:hover:bg-destructive-soft"
            >
              <Mic className="tw:size-6" strokeWidth={1.75} />
            </button>
          )}
        </>
      )}

      <Button
        type="button"
        size="icon"
        onClick={phase === "error" ? handleDelete : handleSend}
        disabled={phase === "loading"}
        aria-label={t("Mandoob:Recorder.Send")}
        className="tw:shrink-0 tw:rounded-full"
      >
        <SendHorizontal
          className="tw:size-5 tw:rtl:-scale-x-100"
          strokeWidth={1.75}
        />
      </Button>
    </div>
  );
}
